import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api, authApi } from '@/lib/api'
import type { PaginatedResponse } from '@/lib/api-types'
import type {
  BillingSubscription,
  CustomField,
  CustomFieldType,
  Department,
  DepartmentAttendanceSettings,
  DepartmentAttendanceSettingsInput,
  IntegrationMapping,
  IntegrationStatus,
  MemberInvitation,
  MemberRole,
  MessagingSettingsInput,
  MessagingSettingsResponse,
  OrgMember,
  OrgUpdateInput,
  UpdatedOrganization,
  UserInvitation,
  WebhookDelivery,
  WebhookEndpoint,
  WorkingHoursDay,
} from '@/types/settings'

export const userInvitationsKey = ['user-invitations'] as const

export const settingsKeys = {
  members: (orgId: string | null) => ['settings', 'members', orgId] as const,
  invitations: (orgId: string | null) =>
    ['settings', 'invitations', orgId] as const,
  departments: (orgId: string | null) =>
    ['settings', 'departments', orgId] as const,
  webhooks: (orgId: string | null) => ['settings', 'webhooks', orgId] as const,
  webhookDeliveries: (orgId: string | null, id: string) =>
    ['settings', 'webhooks', orgId, id, 'deliveries'] as const,
  customFields: (orgId: string | null) =>
    ['settings', 'custom-fields', orgId] as const,
  userGoogleCalendar: (orgId: string | null) =>
    ['settings', 'integrations', 'user-google-calendar', orgId] as const,
  rdStation: (orgId: string | null) =>
    ['settings', 'integrations', 'rd-station', orgId] as const,
  exactSpotter: (orgId: string | null) =>
    ['settings', 'integrations', 'exact-spotter', orgId] as const,
  billing: (orgId: string | null) => ['settings', 'billing', orgId] as const,
  messaging: (orgId: string | null) =>
    ['settings', 'messaging', orgId] as const,
}

export function useMessagingSettings(orgId: string | null) {
  return useQuery<MessagingSettingsResponse>({
    queryKey: settingsKeys.messaging(orgId),
    queryFn: async () => {
      const res = await api.get<MessagingSettingsResponse>(
        `/orgs/${orgId}/messaging-settings`
      )
      return res.data
    },
    enabled: !!orgId,
  })
}

export function useUpdateMessagingSettings(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: MessagingSettingsInput) => {
      const res = await api.put(`/orgs/${orgId}/messaging-settings`, data)
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: settingsKeys.messaging(orgId) })
    },
  })
}

export function useMemberWorkingHours(
  orgId: string | null,
  userId: string | null,
  enabled = true
) {
  return useQuery<WorkingHoursDay[]>({
    queryKey: ['settings', 'working-hours', orgId, userId] as const,
    queryFn: async () => {
      const res = await api.get<WorkingHoursDay[]>(
        `/orgs/${orgId}/members/${userId}/working-hours`
      )
      return res.data
    },
    enabled: !!orgId && !!userId && enabled,
  })
}

export function useUpdateMemberWorkingHours(
  orgId: string | null,
  userId: string | null
) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (days: WorkingHoursDay[]) => {
      const res = await api.put(
        `/orgs/${orgId}/members/${userId}/working-hours`,
        { days }
      )
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['settings', 'working-hours', orgId, userId],
      })
    },
  })
}

export function useOrgMembers(orgId: string | null, enabled = true) {
  return useQuery<PaginatedResponse<OrgMember>>({
    queryKey: settingsKeys.members(orgId),
    queryFn: async () => {
      const res = await api.get<PaginatedResponse<OrgMember>>(
        `/orgs/${orgId}/members`,
        { params: { limit: 200 } }
      )
      return res.data
    },
    enabled: !!orgId && enabled,
  })
}

export function useOrgInvitations(orgId: string | null) {
  return useQuery<MemberInvitation[]>({
    queryKey: settingsKeys.invitations(orgId),
    queryFn: async () => {
      const res = await api.get<MemberInvitation[]>(
        `/orgs/${orgId}/members/invitations`
      )
      return res.data
    },
    enabled: !!orgId,
  })
}

export function useInviteMember(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: {
      email: string
      role: Exclude<MemberRole, 'OWNER'>
      sendEmail?: boolean
    }) => {
      const res = await api.post(`/orgs/${orgId}/members/invite`, data)
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: settingsKeys.members(orgId) })
      void qc.invalidateQueries({ queryKey: settingsKeys.invitations(orgId) })
    },
  })
}

export function useUpdateMemberRole(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      memberId,
      role,
    }: {
      memberId: string
      role: Exclude<MemberRole, 'OWNER'>
    }) => {
      const res = await api.put(`/orgs/${orgId}/members/${memberId}/role`, {
        role,
      })
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: settingsKeys.members(orgId) })
    },
  })
}

export function useCancelInvitation(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (invitationId: string) => {
      await api.delete(`/orgs/${orgId}/members/invitations/${invitationId}`)
      return invitationId
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: settingsKeys.invitations(orgId) })
    },
  })
}

export function useResendInvitation(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (invitationId: string) => {
      await api.post(
        `/orgs/${orgId}/members/invitations/${invitationId}/resend`
      )
      return invitationId
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: settingsKeys.invitations(orgId) })
    },
  })
}

export function useUserInvitations() {
  return useQuery<UserInvitation[]>({
    queryKey: userInvitationsKey,
    queryFn: async () => {
      const res = await api.get<UserInvitation[]>('/users/me/invitations')
      return res.data
    },
  })
}

export function useRejectUserInvitation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (invitationId: string) => {
      await api.post(`/users/me/invitations/${invitationId}/reject`)
      return invitationId
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: userInvitationsKey })
    },
  })
}

export function useRemoveMember(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (memberId: string) => {
      await api.delete(`/orgs/${orgId}/members/${memberId}`)
      return memberId
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: settingsKeys.members(orgId) })
    },
  })
}

export function useDepartments(orgId: string | null, enabled = true) {
  return useQuery<Department[]>({
    queryKey: settingsKeys.departments(orgId),
    queryFn: async () => {
      const res = await api.get<PaginatedResponse<Department>>(
        `/orgs/${orgId}/departments`,
        { params: { limit: 200 } }
      )
      return res.data.data
    },
    enabled: !!orgId && enabled,
  })
}

export function useDepartment(
  orgId: string | null,
  departmentId: string | null
) {
  return useQuery<Department>({
    queryKey: ['settings', 'departments', orgId, departmentId] as const,
    queryFn: async () => {
      const res = await api.get<Department>(
        `/orgs/${orgId}/departments/${departmentId}`
      )
      return res.data
    },
    enabled: !!orgId && !!departmentId,
  })
}

export function useCreateDepartment(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: { name: string; color?: string }) => {
      const res = await api.post<Department>(`/orgs/${orgId}/departments`, data)
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: settingsKeys.departments(orgId) })
    },
  })
}

export function useUpdateDepartment(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: { name?: string; color?: string; isActive?: boolean }
    }) => {
      const res = await api.put<Department>(
        `/orgs/${orgId}/departments/${id}`,
        data
      )
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: settingsKeys.departments(orgId) })
    },
  })
}

export function useDeleteDepartment(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/orgs/${orgId}/departments/${id}`)
      return id
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: settingsKeys.departments(orgId) })
    },
  })
}

export function useAddDepartmentMember(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      deptId,
      memberId,
    }: {
      deptId: string
      memberId: string
    }) => {
      const res = await api.post(
        `/orgs/${orgId}/departments/${deptId}/members`,
        {
          memberId,
        }
      )
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: settingsKeys.departments(orgId) })
    },
  })
}

export function useRemoveDepartmentMember(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      deptId,
      memberId,
    }: {
      deptId: string
      memberId: string
    }) => {
      await api.delete(
        `/orgs/${orgId}/departments/${deptId}/members/${memberId}`
      )
      return { deptId, memberId }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: settingsKeys.departments(orgId) })
    },
  })
}

export function useUpsertDepartmentAttendanceSettings(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      deptId,
      data,
    }: {
      deptId: string
      data: DepartmentAttendanceSettingsInput
    }) => {
      const res = await api.put<DepartmentAttendanceSettings>(
        `/orgs/${orgId}/departments/${deptId}/attendance-settings`,
        data
      )
      return res.data
    },
    onSuccess: (_, { deptId }) => {
      void qc.invalidateQueries({ queryKey: settingsKeys.departments(orgId) })
      void qc.invalidateQueries({
        queryKey: ['settings', 'departments', orgId, deptId],
      })
    },
  })
}

export function useWebhookEndpoints(orgId: string | null) {
  return useQuery<WebhookEndpoint[]>({
    queryKey: settingsKeys.webhooks(orgId),
    queryFn: async () => {
      const res = await api.get<WebhookEndpoint[]>(
        `/orgs/${orgId}/webhook-endpoints`
      )
      return res.data
    },
    enabled: !!orgId,
  })
}

export function useCreateWebhookEndpoint(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: {
      name: string
      endpoint_type: 'inbound' | 'outbound'
      url?: string
      outbound_events?: string[]
    }) => {
      const res = await api.post<WebhookEndpoint>(
        `/orgs/${orgId}/webhook-endpoints`,
        data
      )
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: settingsKeys.webhooks(orgId) })
    },
  })
}

export function useUpdateWebhookEndpoint(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: {
        name?: string
        endpoint_type?: 'inbound' | 'outbound'
        url?: string
        outbound_events?: string[]
        isActive?: boolean
      }
    }) => {
      const res = await api.put<WebhookEndpoint>(
        `/orgs/${orgId}/webhook-endpoints/${id}`,
        data
      )
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: settingsKeys.webhooks(orgId) })
    },
  })
}

export function useDeleteWebhookEndpoint(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/orgs/${orgId}/webhook-endpoints/${id}`)
      return id
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: settingsKeys.webhooks(orgId) })
    },
  })
}

export function useRegenerateWebhookToken(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post<WebhookEndpoint>(
        `/orgs/${orgId}/webhook-endpoints/${id}/regenerate-token`
      )
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: settingsKeys.webhooks(orgId) })
    },
  })
}

export function useWebhookDeliveries(
  orgId: string | null,
  endpointId: string | null
) {
  return useQuery<WebhookDelivery[]>({
    queryKey: settingsKeys.webhookDeliveries(orgId, endpointId ?? ''),
    queryFn: async () => {
      const res = await api.get<WebhookDelivery[]>(
        `/orgs/${orgId}/webhook-endpoints/${endpointId}/deliveries`
      )
      return res.data
    },
    enabled: !!orgId && !!endpointId,
  })
}

export function useReprocessWebhookDelivery(
  orgId: string | null,
  endpointId: string | null
) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (deliveryId: string) => {
      const res = await api.post<WebhookDelivery>(
        `/orgs/${orgId}/webhook-endpoints/${endpointId}/deliveries/${deliveryId}/reprocess`
      )
      return res.data
    },
    onSuccess: () => {
      if (endpointId) {
        void qc.invalidateQueries({
          queryKey: settingsKeys.webhookDeliveries(orgId, endpointId),
        })
      }
    },
  })
}

export function useCustomFields(orgId: string | null) {
  return useQuery<CustomField[]>({
    queryKey: settingsKeys.customFields(orgId),
    queryFn: async () => {
      const res = await api.get<PaginatedResponse<CustomField>>(
        `/orgs/${orgId}/custom-fields`,
        { params: { limit: 200 } }
      )
      return res.data.data
    },
    enabled: !!orgId,
  })
}

export function useCreateCustomField(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: {
      name: string
      key: string
      fieldType: CustomFieldType
      required?: boolean
      options?: string
    }) => {
      const res = await api.post<CustomField>(
        `/orgs/${orgId}/custom-fields`,
        data
      )
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: settingsKeys.customFields(orgId) })
    },
  })
}

export function useUpdateCustomField(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: {
        name?: string
        fieldType?: CustomFieldType
        required?: boolean
        options?: string
      }
    }) => {
      const res = await api.put<CustomField>(
        `/orgs/${orgId}/custom-fields/${id}`,
        data
      )
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: settingsKeys.customFields(orgId) })
    },
  })
}

export function useDeleteCustomField(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/orgs/${orgId}/custom-fields/${id}`)
      return id
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: settingsKeys.customFields(orgId) })
    },
  })
}

export interface CustomFieldMergePreview {
  sourceCount: number
  migrateCount: number
  conflictCount: number
  targetIsSelect: boolean
  selectNoMatch: { count: number; samples: string[] }
}

export function useCustomFieldMergePreview(
  orgId: string | null,
  sourceId: string | null,
  targetId: string | null
) {
  return useQuery({
    queryKey: [
      'custom-field-merge-preview',
      orgId,
      sourceId,
      targetId,
    ] as const,
    enabled: Boolean(orgId && sourceId && targetId),
    queryFn: async () => {
      const res = await api.get<CustomFieldMergePreview>(
        `/orgs/${orgId}/custom-fields/${sourceId}/merge/preview`,
        { params: { targetId } }
      )
      return res.data
    },
  })
}

export function useMergeCustomField(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      sourceId,
      targetId,
    }: {
      sourceId: string
      targetId: string
    }) => {
      await api.post(`/orgs/${orgId}/custom-fields/${sourceId}/merge`, {
        targetId,
      })
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: settingsKeys.customFields(orgId) })
    },
  })
}

// Os hooks do calendário org-level (status/connect/disconnect/calendars/
// setCalendar) foram removidos junto com o próprio conceito: agenda é sempre
// de uma pessoa, e a conexão vive nos hooks `*UserGoogleCalendar*` abaixo.

export function useUserGoogleCalendarStatus(orgId: string | null) {
  return useQuery<{
    connected: boolean
    calendarId: string
    reauthRequired?: boolean
  }>({
    queryKey: settingsKeys.userGoogleCalendar(orgId),
    queryFn: async () => {
      const res = await api.get<{
        connected: boolean
        calendarId: string
        reauthRequired?: boolean
      }>(`/orgs/${orgId}/me/integrations/google-calendar/status`)
      return res.data
    },
    enabled: !!orgId,
  })
}

export function useConnectUserGoogleCalendar(orgId: string | null) {
  return useMutation({
    mutationFn: async () => {
      const res = await api.get<{ url: string }>(
        `/orgs/${orgId}/me/integrations/google-calendar/auth-url`
      )
      window.location.href = res.data.url
      return res.data
    },
  })
}

export function useDisconnectUserGoogleCalendar(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const res = await api.delete(
        `/orgs/${orgId}/me/integrations/google-calendar/disconnect`
      )
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: settingsKeys.userGoogleCalendar(orgId),
      })
    },
  })
}

export function useRdStationStatus(orgId: string | null) {
  return useQuery<IntegrationStatus>({
    queryKey: settingsKeys.rdStation(orgId),
    queryFn: async () => {
      const res = await api.get<IntegrationStatus>(
        `/orgs/${orgId}/integrations/rd-station/status`
      )
      return res.data
    },
    enabled: !!orgId,
  })
}

export function useConfigureRdStation(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: { apiKey?: string; privateToken?: string }) => {
      const res = await api.post(
        `/orgs/${orgId}/integrations/rd-station/config`,
        data
      )
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: settingsKeys.rdStation(orgId) })
      void qc.invalidateQueries({
        queryKey: ['settings', 'integrations', 'rd-station', 'config', orgId],
      })
    },
  })
}

export function useRdStationOAuthAuthUrl(orgId: string | null) {
  return useMutation({
    mutationFn: async () => {
      const res = await api.get<{ url: string }>(
        `/orgs/${orgId}/integrations/rd-station/oauth/auth-url`
      )
      return res.data
    },
  })
}

export function useDisconnectRdStation(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const res = await api.delete(
        `/orgs/${orgId}/integrations/rd-station/disconnect`
      )
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: settingsKeys.rdStation(orgId) })
      void qc.invalidateQueries({
        queryKey: ['settings', 'integrations', 'rd-station', 'config', orgId],
      })
    },
  })
}

export function useRdStationConfig(orgId: string | null) {
  return useQuery<{
    connected: boolean
    authMode: 'oauth' | 'private_token' | null
    hasOAuth: boolean
    hasToken: boolean
    inboundToken: string | null
    mappings: IntegrationMapping[]
    autoSyncCustomFields: boolean
    autoSyncTags: boolean
    tagConversionMap: Record<string, string>
    conversationStatusConversionMap: Record<string, string>
    cadenceConversionMap: Record<string, string>
  }>({
    queryKey: ['settings', 'integrations', 'rd-station', 'config', orgId],
    queryFn: async () => {
      const res = await api.get(`/orgs/${orgId}/integrations/rd-station/config`)
      return res.data
    },
    enabled: !!orgId,
  })
}

export function useSaveRdInboundSettings(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (settings: {
      autoSyncCustomFields?: boolean
      autoSyncTags?: boolean
    }) => {
      const res = await api.put(
        `/orgs/${orgId}/integrations/rd-station/inbound-settings`,
        settings
      )
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['settings', 'integrations', 'rd-station', 'config', orgId],
      })
    },
  })
}

export interface RdOwnerMapping {
  id: string
  ownerEmail: string
  userId: string
}

export function useRdOwnerMappings(orgId: string | null) {
  return useQuery({
    queryKey: [
      'settings',
      'integrations',
      'rd-station',
      'owner-mappings',
      orgId,
    ],
    enabled: Boolean(orgId),
    queryFn: async () => {
      const res = await api.get<RdOwnerMapping[]>(
        `/orgs/${orgId}/integrations/rd-station/owner-mappings`
      )
      return res.data
    },
  })
}

export function useSaveRdOwnerMappings(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (
      mappings: Array<{ ownerEmail: string; userId: string }>
    ) => {
      const res = await api.put(
        `/orgs/${orgId}/integrations/rd-station/owner-mappings`,
        { mappings }
      )
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: [
          'settings',
          'integrations',
          'rd-station',
          'owner-mappings',
          orgId,
        ],
      })
    },
  })
}

export function useTestRdStation(orgId: string | null) {
  return useMutation({
    mutationFn: async (data?: { privateToken?: string }) => {
      const res = await api.post<{
        ok: boolean
        status?: number
        error?: string
      }>(`/orgs/${orgId}/integrations/rd-station/test`, data ?? {})
      return res.data
    },
  })
}

export function useRotateRdInboundToken(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const res = await api.post<{ inboundToken: string }>(
        `/orgs/${orgId}/integrations/rd-station/webhook-token`
      )
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['settings', 'integrations', 'rd-station', 'config', orgId],
      })
    },
  })
}

export function useSaveRdMappings(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (mappings: IntegrationMapping[]) => {
      const res = await api.put(
        `/orgs/${orgId}/integrations/rd-station/mappings`,
        {
          mappings,
        }
      )
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['settings', 'integrations', 'rd-station', 'config', orgId],
      })
    },
  })
}

export function useRdPreviewPayload(orgId: string | null) {
  return useMutation({
    mutationFn: async () => {
      const res = await api.post<{ payload: Record<string, unknown> | null }>(
        `/orgs/${orgId}/integrations/rd-station/preview`
      )
      return res.data
    },
  })
}

export function useRdStatusMap(orgId: string | null) {
  return useQuery<{ statusConversionMap: Record<string, string> }>({
    queryKey: ['settings', 'integrations', 'rd-station', 'status-map', orgId],
    queryFn: async () => {
      const res = await api.get<{
        statusConversionMap: Record<string, string>
      }>(`/orgs/${orgId}/integrations/rd-station/status-map`)
      return res.data
    },
    enabled: !!orgId,
  })
}

export function useSaveRdStatusMap(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (statusConversionMap: Record<string, string>) => {
      const res = await api.put(
        `/orgs/${orgId}/integrations/rd-station/status-map`,
        {
          statusConversionMap,
        }
      )
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: [
          'settings',
          'integrations',
          'rd-station',
          'status-map',
          orgId,
        ],
      })
    },
  })
}

export function useSaveRdTagConversionMap(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (tagConversionMap: Record<string, string>) => {
      const res = await api.put(
        `/orgs/${orgId}/integrations/rd-station/tag-conversion-map`,
        { tagConversionMap }
      )
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['settings', 'integrations', 'rd-station', 'config', orgId],
      })
    },
  })
}

export function useSaveRdConversationStatusConversionMap(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (
      conversationStatusConversionMap: Record<string, string>
    ) => {
      const res = await api.put(
        `/orgs/${orgId}/integrations/rd-station/conversation-status-conversion-map`,
        { conversationStatusConversionMap }
      )
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['settings', 'integrations', 'rd-station', 'config', orgId],
      })
    },
  })
}

export function useSaveRdCadenceConversionMap(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (cadenceConversionMap: Record<string, string>) => {
      const res = await api.put(
        `/orgs/${orgId}/integrations/rd-station/cadence-conversion-map`,
        { cadenceConversionMap }
      )
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['settings', 'integrations', 'rd-station', 'config', orgId],
      })
    },
  })
}

export function useRdLastPayload(orgId: string | null) {
  return useQuery<{
    lastInboundPayload: unknown
    lastInboundAt: string | null
  }>({
    queryKey: ['settings', 'integrations', 'rd-station', 'last-payload', orgId],
    queryFn: async () => {
      const res = await api.get<{
        lastInboundPayload: unknown
        lastInboundAt: string | null
      }>(`/orgs/${orgId}/integrations/rd-station/last-payload`)
      return res.data
    },
    enabled: !!orgId,
  })
}

export type RdSyncLog = {
  id: string
  direction: string
  entityType: string | null
  entityId: string | null
  status: string
  error: string | null
  request: unknown
  response: unknown
  createdAt: string
}

export function useRdSyncLogs(
  orgId: string | null,
  options?: { limit?: number; direction?: string }
) {
  return useQuery<RdSyncLog[]>({
    queryKey: [
      'settings',
      'integrations',
      'rd-station',
      'sync-logs',
      orgId,
      options,
    ],
    queryFn: async () => {
      const params: Record<string, string> = {}
      if (options?.limit) params.limit = String(options.limit)
      if (options?.direction) params.direction = options.direction
      const res = await api.get<RdSyncLog[]>(
        `/orgs/${orgId}/integrations/rd-station/sync-logs`,
        { params }
      )
      return res.data
    },
    enabled: !!orgId,
  })
}

export function useExactSpotterStatus(orgId: string | null) {
  return useQuery<IntegrationStatus>({
    queryKey: settingsKeys.exactSpotter(orgId),
    queryFn: async () => {
      const res = await api.get<IntegrationStatus>(
        `/orgs/${orgId}/integrations/exact-spotter/status`
      )
      return res.data
    },
    enabled: !!orgId,
  })
}

export function useConfigureExactSpotter(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: {
      apiToken?: string
      baseUrl?: string | null
      defaults?: Record<string, unknown>
    }) => {
      const res = await api.post(
        `/orgs/${orgId}/integrations/exact-spotter/config`,
        data
      )
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: settingsKeys.exactSpotter(orgId) })
    },
  })
}

export function useDisconnectExactSpotter(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const res = await api.delete(
        `/orgs/${orgId}/integrations/exact-spotter/disconnect`
      )
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: settingsKeys.exactSpotter(orgId) })
    },
  })
}

export function useTestExactSpotter(orgId: string | null) {
  return useMutation({
    mutationFn: async () => {
      const res = await api.post<{
        ok: boolean
        status?: number
        error?: string
      }>(`/orgs/${orgId}/integrations/exact-spotter/test`)
      return res.data
    },
  })
}

export function useExactSpotterFields(orgId: string | null) {
  return useQuery<{ ok: boolean; fields: unknown[]; error?: string }>({
    queryKey: ['settings', 'integrations', 'exact-spotter', 'fields', orgId],
    queryFn: async () => {
      const res = await api.get(
        `/orgs/${orgId}/integrations/exact-spotter/fields`
      )
      return res.data
    },
    enabled: !!orgId,
  })
}

export function useExactSpotterMappings(orgId: string | null) {
  return useQuery<IntegrationMapping[]>({
    queryKey: ['settings', 'integrations', 'exact-spotter', 'mappings', orgId],
    queryFn: async () => {
      const res = await api.get<IntegrationMapping[]>(
        `/orgs/${orgId}/integrations/exact-spotter/mappings`
      )
      return res.data
    },
    enabled: !!orgId,
  })
}

export function useSaveExactSpotterMappings(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      direction,
      mappings,
    }: {
      direction: 'inbound' | 'outbound'
      mappings: IntegrationMapping[]
    }) => {
      const res = await api.put(
        `/orgs/${orgId}/integrations/exact-spotter/mappings`,
        { direction, mappings }
      )
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: [
          'settings',
          'integrations',
          'exact-spotter',
          'mappings',
          orgId,
        ],
      })
    },
  })
}

export function useBillingSubscription(orgId: string | null) {
  return useQuery<BillingSubscription>({
    queryKey: settingsKeys.billing(orgId),
    queryFn: async () => {
      const res = await api.get<BillingSubscription>(
        `/orgs/${orgId}/billing/subscription`
      )
      return res.data
    },
    enabled: !!orgId,
  })
}

export function useBillingPortalUrl(orgId: string | null) {
  return useMutation({
    mutationFn: async () => {
      const res = await api.get<{ url: string }>(
        `/orgs/${orgId}/billing/portal-url`
      )
      return res.data
    },
  })
}

export function useUpdateOrg(orgId: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: OrgUpdateInput) => {
      const res = await api.put<UpdatedOrganization>(`/orgs/${orgId}`, data)
      return res.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['auth'] })
    },
  })
}

export type UploadPurpose =
  | 'profile-avatar'
  | 'org-logo'
  | 'inbox-media'
  | 'knowledge-document'
  | 'generic'

export type UploadResponse = {
  assetId?: string
  key: string
  url: string
  publicUrl: string
  fileName: string
  mimeType: string
  sizeBytes: number
  sha256?: string
  kind?: string
  purpose?: UploadPurpose | string
  reused?: boolean
}

export function useUploadFile(orgId: string | null) {
  return useMutation({
    mutationFn: async ({
      file,
      purpose,
    }: {
      file: File
      purpose: UploadPurpose
    }) => {
      const form = new FormData()
      form.append('file', file)
      form.append('purpose', purpose)
      const res = await api.post<UploadResponse>(
        `/orgs/${orgId}/uploads`,
        form,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      )
      return res.data
    },
  })
}

export function useUpdateProfile() {
  return useMutation({
    mutationFn: async (data: {
      name?: string
      email?: string
      image?: string | null
    }) => {
      const res = await authApi.post('/api/auth/update-user', data)
      return res.data
    },
  })
}

export function useChangePassword() {
  return useMutation({
    mutationFn: async (data: {
      currentPassword: string
      newPassword: string
    }) => {
      const res = await authApi.post('/api/auth/change-password', data)
      return res.data
    },
  })
}
